import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  fetchPremiumBotConnectedAccounts,
  syncPremiumBotAccountBalances,
  clearPremiumBotCache,
  getCacheStats
} from '@/services/admin/admin-premium-bot-detail-service';

export interface PremiumBotConnectedAccount {
  id: string;
  botId: string;
  tradingAccountId: string;
  volumeMultiplier: number;
  isReversePosition: boolean;
  status: string;
  addedDate: string;
  // User info
  userId: string;
  userName: string;
  userEmail: string;
  userAvatar?: string;
  // Account info
  accountNumber: string;
  balance: number | null;
  isLive: boolean;
  broker?: string;
  propFirm?: string;
  lastBalanceUpdate?: string;
}

export interface PremiumBotAccountsStats {
  totalAccounts: number;
  uniqueUsers: number;
  totalCapital: number;
  reversePositions: number;
  liveAccounts: number;
  demoAccounts: number;
  totalSubscribers: number;
  averageBalance: number;
}

export const usePremiumBotDetailAdmin = (botId: string | undefined) => {
  const queryClient = useQueryClient();

  const {
    data: connectedAccounts = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['premiumBotConnectedAccounts', botId],
    queryFn: () => fetchPremiumBotConnectedAccounts(botId!),
    enabled: !!botId,
  });

  const syncBalancesMutation = useMutation({
    mutationFn: (premiumBotId: string) => syncPremiumBotAccountBalances(premiumBotId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['premiumBotConnectedAccounts', botId] });
      return result;
    },
  });

  const clearCacheMutation = useMutation({
    mutationFn: () => {
      clearPremiumBotCache(botId);
      return Promise.resolve();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['premiumBotConnectedAccounts', botId] });
    },
  });

  // Enhanced stats with premium bot specific metrics
  const accountsStats: PremiumBotAccountsStats = React.useMemo(() => {
    if (!connectedAccounts || connectedAccounts.length === 0) {
      return {
        totalAccounts: 0,
        uniqueUsers: 0,
        totalCapital: 0,
        reversePositions: 0,
        liveAccounts: 0,
        demoAccounts: 0,
        totalSubscribers: 0,
        averageBalance: 0,
      };
    }

    const uniqueUserIds = new Set(connectedAccounts.map(acc => acc.userId));
    const totalCapital = connectedAccounts.reduce((sum, acc) => sum + (acc.balance || 0), 0);
    const reversePositions = connectedAccounts.filter(acc => acc.isReversePosition).length;
    const liveAccounts = connectedAccounts.filter(acc => acc.isLive).length;
    const demoAccounts = connectedAccounts.filter(acc => !acc.isLive).length;
    const averageBalance = connectedAccounts.length > 0 ? totalCapital / connectedAccounts.length : 0;

    return {
      totalAccounts: connectedAccounts.length,
      uniqueUsers: uniqueUserIds.size,
      totalCapital,
      reversePositions,
      liveAccounts,
      demoAccounts,
      totalSubscribers: uniqueUserIds.size, // Subscribers = unique users for premium bots
      averageBalance,
    };
  }, [connectedAccounts]);

  // Enhanced refresh with cache clearing
  const forceRefresh = React.useCallback(async () => {
    clearPremiumBotCache(botId);
    return refetch();
  }, [botId, refetch]);

  return {
    connectedAccounts,
    accountsStats,
    isLoading,
    error,
    refetch,
    forceRefresh,
    syncBalances: syncBalancesMutation.mutateAsync,
    isSyncingBalances: syncBalancesMutation.isPending,
    syncResult: syncBalancesMutation.data,
    clearCache: clearCacheMutation.mutateAsync,
    isClearingCache: clearCacheMutation.isPending,
    cacheStats: getCacheStats(),
  };
};